import { useEffect, useMemo } from 'react';
import { useCartData } from '~shared/features/cart/hooks/useCartData';
import { useCartNextAvailableDate } from '~shared/features/cart/hooks/useCartNextAvailableDate';
import { getOrderAddress } from '~shared/features/checkout/selectors/getOrder';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import getOrigin from '~shared/lib/analytics/src/getOrigin';
import { useSelectorShared } from '~shared/store';

type OrderProperties = {
  delayedAvailabilityReason: string | null | undefined;
  nextAvailableDateFromNow: string | null | undefined;
};

export const useCartViewed = () => {
  const { trackEvent } = useAnalytics();
  const { cartItems } = useCartData();
  const { earliestAvailableDate, earliestAvailableDateReason } = useCartNextAvailableDate();
  const origin = getOrigin();
  const address = useSelectorShared(getOrderAddress);
  const orderProperties: OrderProperties = {
    delayedAvailabilityReason: null,
    nextAvailableDateFromNow: null,
  };

  if (earliestAvailableDate) {
    orderProperties.delayedAvailabilityReason = earliestAvailableDateReason;
    orderProperties.nextAvailableDateFromNow = earliestAvailableDate;
  }

  const { delayedAvailabilityReason, nextAvailableDateFromNow } = orderProperties;
  const cartItemResourceIds = useMemo(() => cartItems.map((item) => item.resource_id), [cartItems]);

  useEffect(() => {
    cartItemResourceIds.map((resourceId) => {
      trackEvent({
        event: EVENTS.CART_VIEWED,
        params: {
          'delayed availability reason': delayedAvailabilityReason,
          'next available date from now': nextAvailableDateFromNow,
          origin,
          facility_id: address?.dispensing_facility_id,
        },
        additionalFields: {
          prescriptionId: resourceId,
        },
      });
    });
  }, [
    trackEvent,
    cartItemResourceIds,
    delayedAvailabilityReason,
    nextAvailableDateFromNow,
    origin,
    address?.dispensing_facility_id,
  ]);
};
